import React, { useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { AdminCharterAPI, charterFileHref } from '../../services/api';
import {
  DOC_TYPE_LABELS, LICENCE_CODES, ownerLabel, formatDay, formatRand, randToCents, sastDateInput, apiErrorMessage,
} from './charterUtils';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none';

const Field = ({ id, label, hint, children }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

/**
 * The form starts from what the operator claimed; admin confirms each fact
 * against the file. Service types are never claimed by the operator, so they
 * start empty: admin reads them off the licence (spec 5.8, 6.4).
 */
function initialForm(doc) {
  return {
    number: doc.number || '',
    issuer: doc.issuer || '',
    expiresAt: sastDateInput(doc.expiresAt),
    serviceTypes: (doc.serviceTypes || []).join(', '),
    licensePlate: doc.licensePlate || '',
    authorisedAreaText: doc.authorisedAreaText || '',
    licenceCode: doc.licenceCode || '',
    insuredRand: doc.insuredAmountCents ? String(doc.insuredAmountCents / 100) : '',
    note: '',
  };
}

/** The verify body for this document type (spec 5.5). */
export function verifyBody(docType, form) {
  const body = { action: 'verify', number: form.number.trim(), issuer: form.issuer.trim() || undefined, expiresAt: form.expiresAt, note: form.note.trim() || undefined };
  if (docType === 'operating_licence') {
    body.serviceTypes = form.serviceTypes.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    body.licensePlate = form.licensePlate.trim();
    body.authorisedAreaText = form.authorisedAreaText.trim() || undefined;
  } else if (docType === 'prdp_p') {
    body.licenceCode = form.licenceCode;
  } else if (docType === 'passenger_liability_insurance') {
    body.insuredAmountCents = randToCents(form.insuredRand);
  }
  return body;
}

/**
 * The review panel (spec 6.4): opens the file by its signed link, records
 * service types (must include charter), issuer, area, plate, number, expiry,
 * licence code and insured amount, then Verify or Reject with a note. A
 * verified document can be withdrawn the same way.
 */
const DocumentReview = ({ doc, onDone, onClose }) => {
  const [form, setForm] = useState(() => initialForm(doc));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const pending = doc.status === 'pending';
  const href = charterFileHref(doc.fileUrl);
  const types = form.serviceTypes.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  // A licence is verified only with the service types and plate read off it.
  const licenceIncomplete = doc.docType === 'operating_licence' && (types.length === 0 || !form.licensePlate.trim());
  const owner = doc.ownerInfo || {};

  const submit = async (body) => {
    setBusy(true);
    setError('');
    try {
      await AdminCharterAPI.reviewDocument(doc._id, body);
      await onDone();
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not save the review'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5" data-testid="document-review">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{DOC_TYPE_LABELS[doc.docType] || doc.docType}</h2>
          <p className="text-sm text-gray-600">{(doc.companyInfo && doc.companyInfo.companyName) || ''} · {ownerLabel(doc)}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X size={18} /></button>
      </div>

      {href
        ? <a href={href} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center text-sm font-medium text-emerald-700 hover:text-emerald-800">Open the file <ExternalLink size={14} className="ml-1" /></a>
        : <p className="mt-3 text-sm text-red-600">The file cannot be opened (storage unavailable).</p>}
      <p className="text-xs text-gray-500">The link works for 15 minutes. Reload the queue for a new one.</p>

      {!pending && (
        <dl className="mt-4 grid grid-cols-2 gap-2 text-sm">
          <dt className="text-gray-500">Number</dt><dd>{doc.number || ''}</dd>
          <dt className="text-gray-500">Expires</dt><dd>{formatDay(doc.expiresAt)}</dd>
          {doc.serviceTypes && doc.serviceTypes.length > 0 && <><dt className="text-gray-500">Service types</dt><dd>{doc.serviceTypes.join(', ')}</dd></>}
          {doc.licensePlate && <><dt className="text-gray-500">Plate</dt><dd>{doc.licensePlate}</dd></>}
          {doc.authorisedAreaText && <><dt className="text-gray-500">Authorised area</dt><dd>{doc.authorisedAreaText}</dd></>}
          {doc.issuer && <><dt className="text-gray-500">Issuer</dt><dd>{doc.issuer}</dd></>}
          {doc.licenceCode && <><dt className="text-gray-500">Licence code</dt><dd>{doc.licenceCode}</dd></>}
          {doc.insuredAmountCents && <><dt className="text-gray-500">Insured</dt><dd>{formatRand(doc.insuredAmountCents)}</dd></>}
        </dl>
      )}

      {pending && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field id="doc-number" label="Document number"><input id="doc-number" className={inputClass} value={form.number} onChange={set('number')} /></Field>
          <Field id="doc-expiry" label="Expiry date"><input id="doc-expiry" type="date" className={inputClass} value={form.expiresAt} onChange={set('expiresAt')} /></Field>
          <Field id="doc-issuer" label="Issuer"><input id="doc-issuer" className={inputClass} value={form.issuer} onChange={set('issuer')} /></Field>
          {doc.docType === 'operating_licence' && (
            <>
              <Field id="doc-plate" label="Number plate on the licence" hint={owner.licensePlate ? `Vehicle's current plate: ${owner.licensePlate}` : null}>
                <input id="doc-plate" className={inputClass} value={form.licensePlate} onChange={set('licensePlate')} />
              </Field>
              <Field id="doc-types" label="Service types (comma separated)" hint={types.includes('charter') ? null : 'Without charter this licence will not count.'}>
                <input id="doc-types" className={inputClass} value={form.serviceTypes} onChange={set('serviceTypes')} />
              </Field>
              <Field id="doc-area" label="Authorised area"><input id="doc-area" className={inputClass} value={form.authorisedAreaText} onChange={set('authorisedAreaText')} /></Field>
            </>
          )}
          {doc.docType === 'prdp_p' && (
            <Field id="doc-code" label="Licence code">
              <select id="doc-code" className={inputClass} value={form.licenceCode} onChange={set('licenceCode')}>
                <option value="">Choose</option>
                {LICENCE_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          )}
          {doc.docType === 'passenger_liability_insurance' && (
            <Field id="doc-insured" label="Insured amount (rand)"><input id="doc-insured" inputMode="decimal" className={inputClass} value={form.insuredRand} onChange={set('insuredRand')} /></Field>
          )}
        </div>
      )}

      <div className="mt-4">
        <Field id="doc-note" label={pending ? 'Note (required to reject)' : 'Reason to withdraw'}>
          <input id="doc-note" className={inputClass} value={form.note} onChange={set('note')} />
        </Field>
      </div>
      {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}
      {pending && licenceIncomplete && <p className="mt-3 text-xs text-gray-500">Record the service types and the number plate from the licence to verify it.</p>}
      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        <button
          onClick={() => submit({ action: 'reject', note: form.note.trim() })}
          disabled={busy || !form.note.trim()}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
        >
          {pending ? 'Reject' : 'Withdraw'}
        </button>
        {pending && (
          <button
            onClick={() => submit(verifyBody(doc.docType, form))}
            disabled={busy || licenceIncomplete}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
          >
            Verify
          </button>
        )}
      </div>
    </div>
  );
};

export default DocumentReview;
